import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import Button from '../../components/Buttons/Button';
import Card from '../../components/Card/Card';
import DataTable from '../../components/DataTable/DataTable';
import { IColInfo } from '../../components/DataTable/Interfaces';
import ButtonBar from '../../components/Fields/ButtonBar';
import Form from '../../components/Fields/Form';
import NumberField from '../../components/Fields/NumberField';
import SelectListField from '../../components/Fields/SelectListField';
import SwitchField from '../../components/Fields/SwitchField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import Title from '../../components/Headers/Title';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from "../../components/Spinner/SpinnerOverlay";
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import { BusinessPartnerTypeEnum } from '../../enums/BusinessPartnerTypeEnum';
import WorkflowTaskModel from '../../models/WorkflowTaskModel';
import DataService from "../../services/data.service";
import { DataResult } from '../../types/Result';
import FormatUtils from '../../utils/FormatUtils';
import Unauthorized from '../Authentication/Unauthorized';

interface IWorkflowTaskSearchCriteria {
    quickSearch?: string
    workflowDefinitionId?: number
    workflowTaskDefinitionId?: number
    projectId?: number
    customerId?: number
    supplierId?: number
    licensorId?: number
    brandId?: number

    pageNumber: number
    pageSize: number
    sortBy: string
}

const MyWorkflowTaskView: React.FC = () => {
    const { user } = useAuth();
    const [formData, setFormData] = useState<IWorkflowTaskSearchCriteria>()
    const [myWaitTasks, setMyWaitTasks] = useState<WorkflowTaskModel[]>([]);
    const [myOtherTasks, setMyOtherTasks] = useState<WorkflowTaskModel[]>([]);
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const { handleResult, handleException } = useNotifications();
    const formatUtils = new FormatUtils()

    const workflowDefinitions = useSelectListOptions(`api/v1/selectlist/WorkflowDefinitionsWithTasksForUser`, '-- All workflows');
    const workflowTaskDefinitions = useSelectListOptions(`api/v1/selectlist/WorkflowTaskDefinitionsWithTasksForUser?workflowDefinitionId=${formData?.workflowDefinitionId ?? 0}`, '-- All tasks');
    const taskCategories = useSelectListOptions('api/v1/selectlist/WorkflowTaskCategories', '-- All categories');
    const projects = useSelectListOptions('api/v1/selectlist/ProjectsWithTasksForUser', '-- All projects');
    const customers = useSelectListOptions('api/v1/selectlist/BusinessPartnersWithTasksForUser?businessPartnerTypeId=' + BusinessPartnerTypeEnum.Customer, '-- All customers');
    const suppliers = useSelectListOptions('api/v1/selectlist/BusinessPartnersWithTasksForUser?businessPartnerTypeId=' + BusinessPartnerTypeEnum.Supplier, '-- All suppliers');
    const licensors = useSelectListOptions('api/v1/selectlist/BusinessPartnersWithTasksForUser?businessPartnerTypeId=' + BusinessPartnerTypeEnum.Licensor, '-- All licensors');
    const brands = useSelectListOptions('api/v1/selectlist/BrandsWithTasksForUser', '-- All brands');

    function submit() {
        getTableData();
    }

    function setDefaultSearchCriteria() {
        var defaultSearchCriteria: IWorkflowTaskSearchCriteria = {
            workflowDefinitionId: 0,
            pageNumber: 1,
            pageSize: 10000,
            sortBy: 'CreatedOn'
        }        

        setFormData({ ...defaultSearchCriteria });
    }

    useEffect(() => {
        const filterItem = localStorage.getItem("workflowMyTasksFilter")
        if (filterItem !== null) {
            let filter: IWorkflowTaskSearchCriteria = JSON.parse(filterItem)
            setFormData((x) => filter)
            getTableDataWithFilter(filter)
        }
        else
            setDefaultSearchCriteria()

    }, [])

    function getTableData() {
        if (formData)
            getTableDataWithFilter(formData)
    }

    function getTableDataWithFilter(filter: IWorkflowTaskSearchCriteria) {
        setShowSpinner(true);
        localStorage.setItem('workflowMyTasksFilter', JSON.stringify(filter));
            DataService
                .post<DataResult<WorkflowTaskModel[]>>(`api/v1/WorkflowTasks/SearchMyWaitTasks/`, filter)
                .then(response => {
                    setShowSpinner(false);
                    handleResult(response);
                    if (response.isSuccesful === true && response.data)
                        setMyWaitTasks(response.data);
                })
                .catch(e => {
                    setShowSpinner(false);
                    handleException(e)
                });

            DataService
                .post<DataResult<WorkflowTaskModel[]>>(`api/v1/WorkflowTasks/SearchMyOtherTasks/`, filter)
                .then(response => {
                    setShowSpinner(false);
                    handleResult(response);
                    if (response.isSuccesful === true && response.data)
                        setMyOtherTasks(response.data);
                })
                .catch(e => {
                    setShowSpinner(false);
                    handleException(e)
                });
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'quickSearch', label: 'Search', labelSize: 3 },
            { name: 'workflowDefinitionId', label: 'Workflow', labelSize: 3 },
            { name: 'workflowTaskDefinitionId', label: 'Task', labelSize: 3 },
            { name: 'taskCategoryId', label: 'Task', labelSize: 3 },
            { name: 'projectId', label: 'Project', labelSize: 3 },
            { name: 'customerId', label: 'Customer', labelSize: 3 },
            { name: 'supplierId', label: 'Supplier', labelSize: 3 },
            { name: 'licensorId', label: 'Licensor', labelSize: 3 },
            { name: 'brandId', label: 'Brand', labelSize: 3 },
            { name: 'pageSize', label: 'Nr of Records', labelSize: 3 },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData);

    const { isAuthorizedForWorkflowDefinition } = useAuth();
    if (!isAuthorizedForWorkflowDefinition(ApplicationFunctions.ViewMyWorkflowTasks))
        return (<Unauthorized />);

    function getStatusColor(rowData: WorkflowTaskModel, colInfo: IColInfo): string | undefined {
        const now = new Date(Date.now())
        const dueOn = formatUtils.dateFromString(rowData.dueOn);
        const completedOn = rowData.completedOn
            ? formatUtils.dateFromString(rowData.completedOn)
            : undefined;

        if (dueOn === undefined || dueOn >= now || (completedOn && completedOn <= dueOn))
            return 'Green';

        return 'red';
    }

    function getItemProject(rowData: WorkflowTaskModel, colInfo: IColInfo): string | undefined {
        if (rowData.workflow.itemRevision && rowData.workflow.project)
            return `${rowData.workflow.projectId} / ${rowData.workflow.itemRevision.item.itemNr}`

        if (rowData.workflow.project)
            return `${rowData.workflow.projectId}`

        if (rowData.workflow.itemRevision)
            return `${rowData.workflow.itemRevision.item.itemNr}`

        return ''
    }

    function getItemProjectName(rowData: WorkflowTaskModel, colInfo: IColInfo): string | undefined {
        if (rowData.workflow.itemRevision && rowData.workflow.project)
            return `${rowData.workflow.project.name} / ${rowData.workflow.itemRevision.item.name}`

        if (rowData.workflow.project)
            return `${rowData.workflow.project.name}`

        if (rowData.workflow.itemRevision)
            return `${rowData.workflow.itemRevision.item.name}`

        return ''
    }


    function onOtherTasksCellChange(value: any, rowIndex: number, dataProp?: string) {
        if (myOtherTasks && dataProp) {
            dataProp.split('.').reduce((object, propertyName, index, elements) => {
                if (index === elements.length - 1) {
                    object[propertyName] = value;
                    setMyOtherTasks(myOtherTasks.slice());
                }

                if (object && object[propertyName]) {
                    return object[propertyName];
                }
                return null;
            }, myOtherTasks[rowIndex]);
        }
    }

    function onWaitTasksCellChange(value: any, rowIndex: number, dataProp?: string) {
        if (myWaitTasks && dataProp) {
            dataProp.split('.').reduce((object, propertyName, index, elements) => {
                if (index === elements.length - 1) {
                    object[propertyName] = value;
                    setMyWaitTasks(myWaitTasks.slice());
                }

                if (object && object[propertyName]) {
                    return object[propertyName];
                }
                return null;
            }, myWaitTasks[rowIndex]);
        }
    }


    function submitWorkflowTask(model: WorkflowTaskModel) {
        setShowSpinner(true);
        DataService
            .update<WorkflowTaskModel>(`api/v1/workflowtasks/`, model)
            .then(response => {
                setShowSpinner(false);
                handleResult(response);
                getTableData();
            })
            .catch(e => {
                setShowSpinner(false);
                handleException(e);
            });
    }

    const otherTasksColInfos: IColInfo[] = [
        { key: true, dataProp: 'id', visible: false },
        { getCellBackgroundColor: getStatusColor, sort: false, width: 5 },
        { dataProp: 'workflow.project.thumbnailUrl', dataType: 'bigimage', sort: false },
        { title: 'Project code', dataProp: 'workflow.projectId', filterInfo: { filterType: 'Textbox' } },
        { title: 'Project name', dataProp: 'workflow.project.name', filterInfo: { filterType: 'Textbox' } },
        { dataProp: 'workflow.itemRevision.thumbnailUrl', dataType: 'bigimage', sort: false },
        { title: 'Item nr', dataProp: 'workflow.itemRevision.item.itemNr', filterInfo: { filterType: 'Textbox' } },
        { title: 'Revision', dataProp: 'workflow.itemRevision.revisionNumber', filterInfo: { filterType: 'Textbox' } },
        { title: 'Item name', dataProp: 'workflow.itemRevision.item.name', filterInfo: { filterType: 'Textbox' } },
        { title: 'Task', dataProp: 'taskDefinition.name', filterInfo: { filterType: 'CheckboxList' } },
        { title: 'Category', dataProp: 'taskCategory', filterInfo: { filterType: 'CheckboxList' } },
        { title: 'Assigned on', dataProp: 'assignedOn', dataType: 'datetime', sort: 'desc' },
        { title: 'Due on', dataProp: 'dueOn', dataType: 'datetime' },
        { title: 'Comments', dataType: 'workflowTask', sort: false, onCellChange: onOtherTasksCellChange, minWidth: 1000, onSubmitWorkflowTask: submitWorkflowTask, filterInfo: { filterType: 'Textbox' } },
    ];

    const waitTasksColInfos: IColInfo[] = [
        { key: true, dataProp: 'id', visible: false },
        { getCellBackgroundColor: getStatusColor, sort: false, width: 5 },
        { dataProp: 'workflow.project.thumbnailUrl', dataType: 'bigimage', sort: false },
        { title: 'Project code', dataProp: 'workflow.projectId', filterInfo: { filterType: 'Textbox' } },
        { title: 'Project name', dataProp: 'workflow.project.name', filterInfo: { filterType: 'Textbox' } },
        { dataProp: 'workflow.itemRevision.thumbnailUrl', dataType: 'bigimage', sort: false },
        { title: 'Item nr', dataProp: 'workflow.itemRevision.item.itemNr', filterInfo: { filterType: 'Textbox' } },
        { title: 'Revision', dataProp: 'workflow.itemRevision.revisionNumber', filterInfo: { filterType: 'Textbox' } },
        { title: 'Item name', dataProp: 'workflow.itemRevision.item.name', filterInfo: { filterType: 'Textbox' } },
        { title: 'Task', dataProp: 'taskDefinition.name', filterInfo: { filterType: 'CheckboxList' } },
        { title: 'Category', dataProp: 'taskCategory', filterInfo: { filterType: 'CheckboxList' } },
        { title: 'Assigned on', dataProp: 'assignedOn', dataType: 'datetime', sort: 'desc' },
        { title: 'Due on', dataProp: 'dueOn', dataType: 'datetime' },
        { title: 'Comments', dataType: 'workflowTask', sort: false, onCellChange: onWaitTasksCellChange, minWidth: 1000, onSubmitWorkflowTask: submitWorkflowTask, filterInfo: { filterType: 'Textbox' } },
    ];

    return (
        <div className='main-content'>
            <Form title='My workflow tasks' footer={(
                <Row>
                    <Col md='6'>
                        <ButtonBar offset={3}>
                            <Button text='Search' {...getSubmitButtonProps()} />
                            <Button text='Clear' type='button' onClick={setDefaultSearchCriteria} />
                        </ButtonBar>
                    </Col>
                    <Col md='6' className='hidden-md'>
                        <ButtonBar offset={0} />
                    </Col>
                </Row>)}>
                <Container fluid>
                    <Row>
                        <Col md='6'>
                            <TextboxField {...getFieldProps('quickSearch')} />
                            <SelectListField {...getFieldProps('workflowDefinitionId')} options={workflowDefinitions} />
                            <SelectListField {...getFieldProps('workflowTaskDefinitionId')} options={workflowTaskDefinitions} />
                            <SelectListField {...getFieldProps('taskCategoryId')} options={taskCategories} />
                            <SelectListField {...getFieldProps('projectId')} options={projects} />
                        </Col>
                        <Col md='6'>
                            <SelectListField {...getFieldProps('customerId')} options={customers} />
                            <SelectListField {...getFieldProps('supplierId')} options={suppliers} />
                            <SelectListField {...getFieldProps('licensorId')} options={licensors} />
                            <SelectListField {...getFieldProps('brandId')} options={brands} />
                        </Col>
                    </Row>
                </Container>
            </Form>
            <Container fluid>
                <Row>
                    <Col>
                        <div className='card' style={{ padding: 12 }}>
                            <Title text='Other Tasks' size={4} />
                            <DataTable
                                name='dtMyWorkflowOtherTasks'
                                colInfos={otherTasksColInfos}
                                tableData={myOtherTasks}
                                pageSize={10}
                                navigateTitle='WorkflowTask'
                                navigateUrl='/plm/Workflow/Task/'
                            />
                         </div>
                    </Col>
                </Row>
            </Container>
            <Container fluid>
                <Row>
                    <Col>
                        <div className='card' style={{padding:12}}>
                            <Title text='Wait Tasks' size={4} />
                            <DataTable
                                name='dtMyWorkflowWaitTasks'
                                colInfos={waitTasksColInfos}
                                tableData={myWaitTasks}
                                pageSize={10}
                                navigateTitle='WorkflowTask'
                                navigateUrl='/plm/Workflow/Task/'
                            />
                        </div>
                    </Col>
                </Row>
            </Container>

            {showSpinner && (<SpinnerOverlay />)}
        </div>
    );
}
export default MyWorkflowTaskView;
